import React, { useState, useEffect } from 'react';
import api, { errorMessage, asArray } from '../api';
import { useSocket } from '../context/SocketContext';
import Avatar from './Avatar';
import { formatSpan } from '../lib/datetime';

/**
 * Delar en träff till vänner eller grupper.
 *
 * Meddelandet bär en referens till träffen, inte inklistrad text, så att
 * kortet i chatten visar aktuell tid och plats även om träffen ändras
 * efteråt. Man kan skicka till flera i rad utan att rutan stängs.
 */
export default function ShareSheet({ meet, onClose }) {
  const { socket, connected } = useSocket();
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sentTo, setSentTo] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get('/friends'), api.get('/group-chats')])
      .then(([f, g]) => {
        if (cancelled) return;
        setFriends(asArray(f.data));
        setGroups(asArray(g.data));
      })
      .catch(err => { if (!cancelled) setError(errorMessage(err, 'Kunde inte hämta dina kontakter.')); });
    return () => { cancelled = true; };
  }, []);

  const send = (key, payload) => {
    if (!socket || !connected) return setError('Ingen kontakt med servern just nu.');
    if (sentTo.includes(key)) return undefined;

    const event = payload.groupId ? 'group:message' : 'chat:message';
    socket.emit(event, { ...payload, meetId: meet.id, content: '' }, (response) => {
      if (response && !response.ok) setError(response.error);
      else setSentTo(prev => [...prev, key]);
    });
    return undefined;
  };

  const rows = [
    ...friends.map(f => ({
      key: `dm-${f.id}`, name: f.username, picture: f.profile_picture,
      payload: { to: f.id },
    })),
    ...groups.map(g => ({
      key: `group-${g.id}`, name: g.name, picture: null,
      payload: { groupId: g.id }, note: `${g.member_count} medlemmar`,
    })),
  ];

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet sheet--list" onClick={e => e.stopPropagation()}>
        <h2 className="sheet__title">Dela träffen</h2>
        <p className="sheet__text">
          <strong>{meet.title}</strong><br />
          {formatSpan(meet.event_date, meet.end_date)}
        </p>

        {error && <p className="notice notice--error">{error}</p>}

        <div className="share-list">
          {rows.length === 0 && <p className="empty">Du har inga vänner eller grupper att dela med ännu.</p>}

          {rows.map(row => {
            const sent = sentTo.includes(row.key);
            return (
              <button
                key={row.key}
                className="convo"
                onClick={() => send(row.key, row.payload)}
                disabled={sent}
              >
                <Avatar src={row.picture} name={row.name} size="sm" />
                <span className="convo__body">
                  <span className="convo__name">{row.name}</span>
                  {row.note && <span className="convo__preview">{row.note}</span>}
                </span>
                <span className={sent ? 'share-state share-state--done' : 'share-state'}>
                  {sent ? 'Skickat ✓' : 'Skicka'}
                </span>
              </button>
            );
          })}
        </div>

        <button className="btn btn--outline btn--block" onClick={onClose}>Klar</button>
      </div>
    </div>
  );
}
